import { useCallback, useRef, useState } from 'react';

import axios from 'axios';

import setupPolling from 'utilities/setupPolling';

import Upload from 'components/helpers/Upload';

const POLLING_INTERVAL_MS = 1000;

export default function useProfilePhotoUploading({ getCurrentProfilePhoto }) {
  const [isProfilePhotoUploading, setIsProfilePhotoUploading] = useState(false);
  const photoUploadLoadingRef = useRef(false);

  // we are using a combination of useState and useRef here because we need useState for re-renders
  // but it will be stale in async callbacks, so we need useRef for the latest value
  const resetProfilePhotoUploadState = () => {
    photoUploadLoadingRef.current = false;
    setIsProfilePhotoUploading(false);
  };

  const createAndProcessProfilePhotoForUploadedPhoto = useCallback(
    (uploadKey) =>
      new Promise((resolve, reject) => {
        const currentProfilePhoto = getCurrentProfilePhoto();

        axios
          .post('/profile_photos', {
            profile_photo: {
              upload_key: uploadKey,
              crop_x: currentProfilePhoto.cropX,
              crop_y: currentProfilePhoto.cropY,
              crop_dimension: currentProfilePhoto.cropDimension,
              rotation: currentProfilePhoto.rotation,
            },
          })
          .then((response) => {
            if (!photoUploadLoadingRef.current) {
              reject();
            }

            setupPolling({
              requestFn: () =>
                axios.get(
                  `/profile_photos/${response.data.data.attributes.hashid}`,
                ),
              testFn: (photoResponse) =>
                photoResponse.data.data.attributes.processed,
              intervalTime: POLLING_INTERVAL_MS,
            })
              .startPolling()
              .then((photoResponse) => {
                resolve(photoResponse.data.data);
              })
              .catch(() => {
                reject(new Error('Profile photo processing failed'));
              });
          })
          .catch((error) => reject(error));
      }),
    [getCurrentProfilePhoto],
  );

  const processProfilePhoto = useCallback(
    (tempPhotoFile, { onError, onSuccess } = {}) => {
      photoUploadLoadingRef.current = true;
      setIsProfilePhotoUploading(true);

      new Upload({
        file: tempPhotoFile,
        customType: 'profile_photo',
        onUpload: (_fileType, uploadKey) => {
          if (!photoUploadLoadingRef.current) return;

          createAndProcessProfilePhotoForUploadedPhoto(uploadKey)
            .then((profilePhotoData) => {
              if (!photoUploadLoadingRef.current) return;

              resetProfilePhotoUploadState();
              onSuccess(profilePhotoData);
            })
            .catch((error) => {
              resetProfilePhotoUploadState();
              onError(error instanceof Error ? error : new Error(error));
            });
        },
        onError: (error) => {
          resetProfilePhotoUploadState();
          onError(error instanceof Error ? error : new Error(error));
        },
      }).start();
    },
    [createAndProcessProfilePhotoForUploadedPhoto],
  );

  return {
    isProfilePhotoUploading,
    processProfilePhoto,
    resetProfilePhotoUploadState,
  };
}
